import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authApi } from '../../lib/api';
import { useSessionStore } from '../../stores/session';
import { Field, TextInput } from '../../components/Field';
import { ErrorBanner } from '../../components/ErrorBanner';

export function ChangePasswordPage() {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const user = useSessionStore((s) => s.user);
  const navigate = useNavigate();

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (newPassword !== confirm) {
      setError(new Error('New password and confirmation do not match.'));
      return;
    }
    setBusy(true);
    try {
      const firstSession = Boolean(user?.mustChangePassword);
      await authApi.changePassword(currentPassword, newPassword);
      useSessionStore.setState((s) => (s.user ? { user: { ...s.user, mustChangePassword: false } } : s));
      navigate(firstSession ? '/onboarding' : '/', { replace: true });
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex h-screen items-center justify-center bg-[var(--bg)] px-4">
      <div className="w-full max-w-[360px]">
        <div className="mb-4 border-b border-[var(--border)] pb-3">
          <h1 className="text-[15px] font-semibold">Set a new password</h1>
          <p className="mt-1 text-[12px] text-[var(--text-dim)]">
            {user?.mustChangePassword
              ? 'Your account was created with a temporary password. Choose a new one to continue; next, Khata will guide you through company setup and your first balanced posting.'
              : 'Change your password.'}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col gap-3" noValidate>
          {error ? <ErrorBanner error={error} onDismiss={() => setError(null)} /> : null}

          <Field label="Current password" htmlFor="cp-current" required>
            <TextInput
              id="cp-current"
              type="password"
              autoFocus
              autoComplete="current-password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              required
            />
          </Field>
          <Field label="New password" htmlFor="cp-new" required hint="At least 8 characters.">
            <TextInput
              id="cp-new"
              type="password"
              autoComplete="new-password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              minLength={8}
            />
          </Field>
          <Field label="Confirm new password" htmlFor="cp-confirm" required>
            <TextInput
              id="cp-confirm"
              type="password"
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
            />
          </Field>

          <button
            type="submit"
            disabled={busy}
            className="mt-1 rounded-[3px] border border-[var(--accent)] bg-[var(--accent)] px-3 py-1.5 text-[12.5px] font-medium text-[var(--accent-fg)] disabled:opacity-50"
          >
            {busy ? 'Saving…' : 'Save and continue'}
          </button>
        </form>
      </div>
    </div>
  );
}
